import React from 'react';
import useNetworkStatus from './hooks/useNetworkStatus';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './utils/ToastContext';
import ErrorBoundary from './pages/shared/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import { useHostContext } from './hooks/useHostContext';
import {
  AdminHostRoutes,
  RiderHostRoutes,
  MerchantHostRoutes,
  CustomerHostRoutes,
  CompanyAuthRoutes,
  CompanyGuestHostRoutes,
  ApexHostRoutes,
} from './routing/AppRouteTrees';
import TrialEndedScreen from './components/TrialEndedScreen';

function LoadingFallback() {
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      height: '100vh', background: '#f5f5f5',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: '3px solid #e0e0e0', borderTopColor: '#3E2C6E',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const hostCtx = useHostContext();

  function getDefaultRoute() {
    if (!user) return '/login';
    switch (user.type) {
      case 'system_admin': return '/admin';
      case 'merchant': return '/merchant';
      case 'rider': return hostCtx.portal === 'rider' ? '/' : '/rider';
      default: return '/';
    }
  }

  if (hostCtx.loading || authLoading) return <LoadingFallback />;

  if (isAuthenticated && user?.type && user.type !== 'system_admin' && user.access_status === 'trial_ended') {
    return <TrialEndedScreen />;
  }

  if (hostCtx.portal === 'admin') {
    return <AdminHostRoutes isAuthenticated={isAuthenticated} />;
  }

  if (hostCtx.portal === 'rider' && hostCtx.companySlug) {
    return <RiderHostRoutes isAuthenticated={isAuthenticated} />;
  }

  if (hostCtx.portal === 'merchant' && hostCtx.companySlug) {
    return <MerchantHostRoutes isAuthenticated={isAuthenticated} />;
  }

  if (hostCtx.portal === 'customer' && hostCtx.companySlug) {
    return <CustomerHostRoutes isAuthenticated={isAuthenticated} user={user} />;
  }

  if (hostCtx.portal === 'company' && hostCtx.companySlug) {
    return isAuthenticated
      ? <CompanyAuthRoutes />
      : <CompanyGuestHostRoutes />;
  }

  return (
    <ApexHostRoutes
      isAuthenticated={isAuthenticated}
      getDefaultRoute={getDefaultRoute}
    />
  );
}

export default function App() {
  const isOnline = useNetworkStatus();

  return (
    <AuthProvider>
      <ToastProvider>
        <ErrorBoundary>
          <div style={styles.app}>
            {!isOnline && <div style={styles.offlineBanner}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
                <path d="M8 3v6M8 12v.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              </svg>
              أنت غير متصل بالإنترنت — بعض الخدمات قد لا تعمل
            </div>}
            <React.Suspense fallback={<LoadingFallback />}>
              <AppRoutes />
            </React.Suspense>
            <InstallPrompt />
          </div>
        </ErrorBoundary>
      </ToastProvider>
    </AuthProvider>
  );
}

const styles = {
  app: {
    direction: 'rtl',
    textAlign: 'right',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  offlineBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    backgroundColor: '#ff9800',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 500,
    textAlign: 'center',
    direction: 'rtl',
  },
};